import {
  Box,
  Button,
  Divider,
  Flex,
  SegmentedControl,
  Select,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconChevronLeft, IconDeviceFloppy, IconPlus, IconX } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { invoiceCurrencyOptions, invoiceTypeLabel, invoiceTypeOf } from '../../helpers/invoiceDisplay';
import useFetch from '../../helpers/useFetch';
import DatePickerControl from '../DatePicker';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

const recalculateDebounced = AwesomeDebouncePromise((fetch, invoiceReduced) => {
  return fetch(`/api/admin/invoices/recalculate`, {
    method: 'POST',
    body: JSON.stringify({ invoice: JSON.parse(invoiceReduced) }),
  });
}, 300);

const InvoiceForm = ({ invoice, setInvoice, save, reason, setReason }) => {
  const fetch = useFetch();
  const [userError, setUserError] = useState();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();

  // /recalculate answers with `new Invoice(body)`, so every field this omits comes
  // back as a model default — leaving `type` out silently undoes a type switch.
  const invoiceReduced = useMemo(
    () =>
      invoice
        ? JSON.stringify({
            accountId: invoice.accountId,
            number: invoice.number,
            currency: invoice.currency,
            day: invoice.day,
            type: invoiceTypeOf(invoice),
            proformaInvoiceId: invoice.proformaInvoiceId ?? null,
            templateData: {
              ...invoice.templateData,
              lineItems: invoice.templateData.lineItems.map((li) => {
                return {
                  quantity: li.quantity,
                  unitPrice: li.unitPrice,
                  description: li.description,
                  domainId: li.domainId,
                };
              }),
              recalculatedAt: null,
            },
          })
        : undefined,
    [invoice]
  );

  useEffect(() => {
    if (!invoiceReduced) {
      return;
    }

    setLoading(true);
    recalculateDebounced(fetch, invoiceReduced)
      .then(({ invoice }) => {
        setUserError(undefined);
        setInvoice({ ...invoice, id });
      })
      .catch(setUserError)
      .finally(() => setLoading(false));
  }, [fetch, invoiceReduced, id, setUserError, setInvoice]);

  const onLineItemChange = (index, { target }) => {
    const lineItems = JSON.parse(JSON.stringify(invoice.templateData.lineItems));
    lineItems[index][target.name] = target.value;
    setInvoice({ ...invoice, templateData: { ...invoice.templateData, lineItems } });
  };

  const onlineItemPriceChange = (index, { target }) => {
    const lineItems = JSON.parse(JSON.stringify(invoice.templateData.lineItems));
    lineItems[index][target.name].price = target.value;
    setInvoice({ ...invoice, templateData: { ...invoice.templateData, lineItems } });
  };

  if (invoice === undefined) {
    return '';
  }

  const currencySelect = (
    <Select
      size="sm"
      w={200}
      label="Currency"
      description="Amounts are not converted"
      allowDeselect={false}
      data={invoiceCurrencyOptions(invoice.currency)}
      value={invoice.currency}
      onChange={(value) =>
        setInvoice({
          ...invoice,
          currency: value,
          templateData: {
            ...invoice.templateData,
            // Every line, a hand-added one too: the PDF cannot price a line without a
            // currency, and a failed render leaves the draft's PDF in place.
            lineItems: invoice.templateData.lineItems.map((li) => ({
              ...li,
              unitPrice: { ...li.unitPrice, currency: value },
              ...(li.totalPrice && { totalPrice: { ...li.totalPrice, currency: value } }),
            })),
          },
        })
      }
    />
  );

  // A new invoice has nothing to render until /recalculate first succeeds, so a
  // refusal (an account billed in a currency the API no longer prices) has to stay
  // visible and fixable here rather than leave the spinner up.
  if (!invoice.templateData.recalculatedAt) {
    return userError ? (
      <>
        <ErrorMessage message={userError.message} />
        {currencySelect}
      </>
    ) : (
      <Spinner />
    );
  }

  if (invoice === null) {
    return <ErrorMessage message="Invoice does not exist!" />;
  }

  const type = invoiceTypeOf(invoice);
  const typeLabel = invoiceTypeLabel(type);

  return (
    <>
      <Box pb="md" display="flex" style={{ justifyContent: 'space-between' }}>
        <Box>
          <Title order={1}>
            {invoice.number ? `${typeLabel} # ${invoice.number}` : `Draft ${typeLabel.toLowerCase()}`}
          </Title>
          <Flex align="center" gap="md" wrap="wrap" my="xs">
            <SegmentedControl
              size="sm"
              value={type}
              onChange={(value) => setInvoice({ ...invoice, type: value })}
              data={[
                { value: 'invoice', label: 'Invoice' },
                { value: 'proforma', label: 'Proforma invoice' },
              ]}
            />
            {currencySelect}
          </Flex>
          <Title order={2}>Original</Title>
          <Flex align="center" gap="sm" mb="xs">
            <Text size="sm" fw={500}>
              Date of issue:
            </Text>
            <DatePickerControl
              size="sm"
              placeholder="Any date"
              selected={new Date(invoice.day)}
              onChange={(value) => {
                setInvoice({ ...invoice, day: value ? value.toISOString().split('T')[0] : null });
              }}
            />
          </Flex>
          {type === 'invoice' && (
            <Flex align="center" gap="sm" mb="xs">
              <Text size="sm" fw={500}>
                Date of tax event:
              </Text>
              <DatePickerControl
                size="sm"
                disabled={true}
                placeholder="Any date"
                value={invoice.templateData.dateofTaxEvent}
              />
            </Flex>
          )}
          <Flex align="center" gap="sm" mb="xs">
            <Text size="sm" fw={500}>
              Due date:
            </Text>
            <DatePickerControl size="sm" disabled={true} placeholder="Any date" value={invoice.templateData.dueDate} />
          </Flex>
        </Box>
        <Box>
          <Title order={2}>Bill to:</Title>
          <Title order={5}>
            {invoice.templateData.billTo.name}
            <br />
            {invoice.templateData.billTo.address.line1}
            <br />
            {invoice.templateData.billTo.address.line2}
            <br />
            {invoice.templateData.billTo.address.city} {invoice.templateData.billTo.address.postalCode}
            <br />
            {invoice.templateData.billTo.address.country}
            <br />
            {invoice.templateData.billTo.taxNumber && (
              <>
                Tax Number: {invoice.templateData.billTo.taxNumber}
                <br />
              </>
            )}
            {invoice.templateData.billTo.vatNumber && (
              <>
                VAT Number: {invoice.templateData.billTo.vatNumber}
                <br />
              </>
            )}
            Contact: {invoice.templateData.billTo.contactName}
            <br />
          </Title>
        </Box>
      </Box>
      <Divider my="sm" />
      <Table striped withTableBorder highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Description</Table.Th>
            <Table.Th>Quantity</Table.Th>
            <Table.Th>Unit price</Table.Th>
            <Table.Th>Total price</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {invoice.templateData.lineItems.map((lineItem, i) => {
            return (
              <Table.Tr key={i}>
                <Table.Td>
                  <TextInput
                    size="sm"
                    name="description"
                    type="text"
                    value={lineItem.description}
                    onChange={(e) => onLineItemChange(i, e)}
                  />
                </Table.Td>
                <Table.Td style={{ width: '100px' }}>
                  <TextInput
                    size="sm"
                    name="quantity"
                    type="number"
                    step="1"
                    min="0"
                    value={lineItem.quantity}
                    onChange={(e) => onLineItemChange(i, e)}
                  />
                </Table.Td>
                <Table.Td style={{ width: '100px' }}>
                  <TextInput
                    size="sm"
                    name="unitPrice"
                    type="number"
                    value={lineItem.unitPrice.price}
                    onChange={(e) => onlineItemPriceChange(i, e)}
                  />
                </Table.Td>
                <Table.Td style={{ width: '100px' }}>
                  {Intl.NumberFormat(Navigator.language, {
                    style: 'currency',
                    currency: invoice.currency,
                  }).format(lineItem.totalPrice.price)}
                </Table.Td>

                <Table.Td ta="right" style={{ width: '0px' }}>
                  <Tooltip label="Remove line item" position="top">
                    <Button
                      size="sm"
                      color="red"
                      disabled={loading}
                      onClick={() => {
                        setInvoice({
                          ...invoice,
                          templateData: {
                            ...invoice.templateData,
                            lineItems: invoice.templateData.lineItems.filter((_, index) => index !== i),
                          },
                        });
                      }}
                    >
                      <IconX size={14} />
                    </Button>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Box display="flex" style={{ justifyContent: 'flex-end' }}>
        <Button
          variant="default"
          disabled={loading}
          onClick={() => {
            setInvoice({
              ...invoice,
              templateData: {
                ...invoice.templateData,
                lineItems: [
                  ...invoice.templateData.lineItems,
                  {
                    description: '',
                    quantity: 1,
                    unitPrice: { price: 0, currency: invoice.currency },
                    totalPrice: { price: 0, currency: invoice.currency },
                  },
                ],
              },
            });
          }}
        >
          <IconPlus size={14} /> Add line
        </Button>
      </Box>
      <hr />
      <div style={{ textAlign: 'right', paddingTop: 'var(--mantine-spacing-md)' }}>
        Base amount:{' '}
        {parseFloat(invoice.templateData.totals.customerCurrency.baseAmount).toLocaleString(navigator.language, {
          style: 'currency',
          currency: invoice.currency,
        })}
      </div>
      <div style={{ textAlign: 'right', paddingTop: 'var(--mantine-spacing-md)' }}>
        VAT:{' '}
        {parseFloat(invoice.templateData.totals.customerCurrency.vat).toLocaleString(navigator.language, {
          style: 'currency',
          currency: invoice.currency,
        })}
      </div>
      <Title order={5} ta="right" pt="md">
        Total amount due:{' '}
        {parseFloat(invoice.templateData.totals.customerCurrency.finalAmount).toLocaleString(navigator.language, {
          style: 'currency',
          currency: invoice.currency,
        })}
      </Title>
      <Divider my="sm" />
      <Box>
        {userError && <ErrorMessage message={userError.message} />}
        <Flex wrap="wrap" align="flex-end" gap="md" justify="space-between">
          <Button variant="default" component="a" href="#/superAdmin">
            <IconChevronLeft size={14} /> Back
          </Button>
          {setReason && (
            <Textarea
              label="Reason for change"
              placeholder="Optional"
              maxLength={1000}
              autosize
              minRows={1}
              style={{ flexGrow: 1 }}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
            />
          )}
          <Button disabled={loading || !!userError} onClick={() => save(setLoading)}>
            <IconDeviceFloppy size={14} /> Save changes
          </Button>
        </Flex>
      </Box>
    </>
  );
};
export default InvoiceForm;
